import { useState, useMemo, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";

import { Box, CircularProgress, Button, Typography, IconButton, Stack, Divider } from "@mui/material";
import { Add, Refresh, Download, ErrorOutline } from "@mui/icons-material";

import { useBatchList } from "../hooks/useBatchList";
import { MetaDrivenTable, PaginationControls, SearchField, StatusChip, DialogBox } from "../../../shared/components";
import { ROUTES } from "../../../shared/utils";
import { ImportDialog } from "../components/ImportDialog";
import { useHandlePreSign } from "../../../hooks/useHandlePreSign";
import { ImportErrorsDrawer } from "../components/ImportErrorsDrawer";
import { useCapabilities } from "../../../hooks/useCapabilities";

// Constants
const BATCH_STATUS = {
    COMPLETED: 'COMPLETED',
    COMPLETED_WITH_WARNINGS: 'COMPLETED_WITH_WARNINGS',
    PROCESSING: 'PROCESSING',
    PENDING: 'PENDING',
    ERROR: 'ERROR',
    FAILED: 'FAILED',
    PROCESSED: 'PROCESSED'
} as const;

const TEMPLATE_TYPE = {
    SITE: 'site',
    PROJECT: 'project'
} as const;

// Types
interface BatchRow {
    id: number;
    batch_id: string;
    template_type: string;
    status: string;
    file_name: string;
    s3_key: string | null;
    created_date: string;
    total_rows: number;
    processed_rows: number;
    successful_rows: number;
    failed_rows: number;
    warning_count: number;
    errors?: string[];
    batch_reference?: string;
}

interface ErrorDrawerState {
    status: boolean;
    type: string | null;
}

type StatusColor = 'green' | 'blue' | 'red' | 'amber' | 'default' | 'violet';

// Utility functions
const getTemplateTypeColor = (templateType: string): StatusColor => {
    if (templateType === TEMPLATE_TYPE.SITE) return 'green';
    if (templateType === TEMPLATE_TYPE.PROJECT) return 'blue';
    return 'default';
};

const getStatusColor = (status: string): StatusColor => {
    const normalizedStatus = status.toUpperCase();
    if (normalizedStatus === BATCH_STATUS.COMPLETED || normalizedStatus === BATCH_STATUS.PROCESSED) {
        return 'green';
    }
    if (normalizedStatus === BATCH_STATUS.ERROR || normalizedStatus === BATCH_STATUS.FAILED) {
        return 'red';
    }
    if (normalizedStatus === BATCH_STATUS.PROCESSING || normalizedStatus === BATCH_STATUS.PENDING) {
        return 'amber';
    }
    return 'default';
};

const isCompletedStatus = (status: string): boolean => {
    const normalizedStatus = status?.toLowerCase();
    return normalizedStatus === BATCH_STATUS.COMPLETED.toLowerCase() ||
        normalizedStatus === BATCH_STATUS.COMPLETED_WITH_WARNINGS.toLowerCase();
};

export const Import = () => {

    const navigate = useNavigate();

    const { can } = useCapabilities();

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);

    const [searchDraft, setSearchDraft] = useState<string>('');
    const [searchKey, setSearchKey] = useState<string>('');

    const [importDialogOpen, setImportDialogOpen] = useState(false);

    const headers = [
        { alias_name: "batch_id", label: "Batch ID", display_order: 1, is_show: true },
        { alias_name: "template_type", label: "Template Type", display_order: 2, is_show: true },
        { alias_name: "status", label: "Status", display_order: 3, is_show: true },
        { alias_name: "file_name", label: "File Name", display_order: 4, is_show: true },
        { alias_name: "total_rows", label: "Total Rows", display_order: 5, is_show: true },
        { alias_name: "processed_rows", label: "Processed Rows", display_order: 6, is_show: true },
        { alias_name: "failed_rows", label: "Failed Rows", display_order: 7, is_show: true },
        { alias_name: "successful_rows", label: "Error / Warning List", display_order: 8, is_show: true },
        { alias_name: "created_by", label: "Created By", display_order: 9, is_show: true },
        { alias_name: "created_date", label: "Upload Date", display_order: 10, is_show: true },
    ];

    const handleNavigateToBatchDetail = (batchId: number) => {
        navigate(ROUTES.IMPORT_BATCH_DETAIL.replace(':batchHeaderId', String(batchId)));
    };

    const { loading, handleDownload } = useHandlePreSign({});

    const handleDownloadFile = async (row: { file_name: string; s3_key: string | null; template_type: string }) => {
        const downloadParams = {
            type: row.template_type,
            name: row.file_name,
            s3_path: row.s3_key,
        };
        await handleDownload(downloadParams);
    };

    const [errorDrawerState, setErrorDrawerState] = useState<ErrorDrawerState>({
        status: false,
        type: null,
    });

    const [selectedErrors, setSelectedErrors] = useState<string[]>([]);
    const [selectedBatchId, setSelectedBatchId] = useState<string>('');

    const { data, isLoading, pagination, refetch } = useBatchList({
        page,
        limit,
        searchKey,
    });

    const isAllowedToImport = useMemo(() => can("operational_delivery:import_manager:import"), [can]);
    const isAllowedToViewDetails = useMemo(() => can("operational_delivery:import_manager_details:view"), [can]);

    const handleOpenErrorDrawer = (
        errors: string[],
        status: string,
        batchId: string,
    ) => {
        setSelectedErrors(errors);
        setSelectedBatchId(batchId);
        setErrorDrawerState({
            status: true,
            type: status === BATCH_STATUS.COMPLETED_WITH_WARNINGS ? 'Warnings' : 'Errors',
        });
    };

    const handleCloseErrorDrawer = () => {
        setErrorDrawerState({
            status: false,
            type: null,
        });
        setSelectedErrors([]);
        setSelectedBatchId('');
    };

    const renderErrorsSummary = (row: BatchRow) => {
        if (row.status === BATCH_STATUS.PROCESSING) {
            return <Typography variant="body2">Processing...</Typography>;
        }

        const buttonColor =
            row.status === BATCH_STATUS.COMPLETED ? 'success' :
                row.status === BATCH_STATUS.COMPLETED_WITH_WARNINGS ? 'warning' :
                    'error';

        const hasErrors = row.errors && row.errors.length > 0;

        return (
            <Button
                variant="text"
                color={buttonColor}
                size="small"
                startIcon={<ErrorOutline />}
                onClick={() => hasErrors && handleOpenErrorDrawer(row.errors!, row.status, row.batch_id)}
                disabled={!hasErrors}
                sx={{ textTransform: 'none' }}
            >
                {row.successful_rows} successful, {row.failed_rows} failed, {row.warning_count} warnings
            </Button>
        );
    };

    const metrics = useMemo(() => ({
        batch_id: {
            type: 'component' as const,
            element: (row: BatchRow) => {
                const completed = isCompletedStatus(row.status);
                return (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2">{row.batch_id}</Typography>
                        {completed && isAllowedToViewDetails && (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleNavigateToBatchDetail(row.id)}
                                sx={{
                                    fontSize: '0.75rem',
                                    minWidth: 'auto',
                                    px: 1,
                                    py: 0.5,
                                }}
                            >
                                View Records
                            </Button>
                        )}
                    </Stack>
                );
            },
        },
        template_type: {
            type: "component" as const,
            element: (row: BatchRow) => (
                <StatusChip
                    label={row.template_type}
                    color={getTemplateTypeColor(row.template_type)}
                />
            )
        },
        status: {
            type: "component" as const,
            element: (row: BatchRow) => (
                <StatusChip
                    label={row.status}
                    color={getStatusColor(row.status)}
                />
            )
        },
        created_date: {
            type: "date_and_time" as const
        },
        file_name: {
            type: 'component' as const,
            element: (row: BatchRow) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2">
                        {row.file_name}
                    </Typography>
                    {row.s3_key && (
                        <IconButton
                            size="small"
                            onClick={() => handleDownloadFile(row)}
                            disabled={loading}
                            sx={{ color: 'primary.main' }}
                            aria-label={`Download ${row.file_name}`}
                        >
                            <Download fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            ),
        },
        total_rows: {
            type: 'component' as const,
            element: (row: BatchRow) => renderErrorsSummary(row),
        },
    }), [loading, isAllowedToViewDetails]);

    if (isLoading) {
        return (
            <Box height="100%" display="flex" alignItems="center" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    };

    return (
        <Box py={2} display="flex" flexDirection="column" height="100%" overflow="hidden">

            <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, mb: 2 }}>

                <SearchField
                    searchTerm={searchDraft}
                    handleSearch={(e: ChangeEvent<HTMLInputElement>) => {
                        setSearchDraft(e.target.value);
                    }}
                    handleClearSearch={() => {
                        setSearchDraft('');
                        setSearchKey('');
                    }}
                    handleApplySearch={() => {
                        setSearchKey(searchDraft);
                    }}
                />

                <Box display={'flex'} gap={2} alignItems="center">

                    <Button
                        variant="text"
                        color="primary"
                        onClick={() => refetch()}
                        startIcon={<Refresh />}
                    >
                        Refresh
                    </Button>

                    {
                        isAllowedToImport && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setImportDialogOpen(true)}
                                startIcon={<Add />}
                            >
                                Import New File
                            </Button>
                        )
                    }

                </Box>

            </Box>

            <Divider />

            <MetaDrivenTable
                headers={headers}
                data={data}
                dataMetrics={metrics}
                sortDisabled
            />

            {pagination && (
                <Box flexShrink={0} px={2}>
                    <PaginationControls
                        total={pagination.total}
                        rowsPerPage={pagination.limit}
                        currentPage={pagination.page}
                        handlePageChange={(newPage) => {
                            setPage(newPage)
                        }}
                        handleRowsPerPage={(newLimit) => {
                            setPage(1)
                            setLimit(newLimit)
                        }}
                    />
                </Box>
            )}

            <DialogBox
                showModal={importDialogOpen}
                title="Bulk Import"
                subTitle="Upload a spreadsheet to add or update multiple records in bulk."
                onModalClose={() => setImportDialogOpen(false)}
                fullWidth
            >
                <ImportDialog
                    onClose={() => setImportDialogOpen(false)}
                    onUploadComplete={refetch}
                />
            </DialogBox>

            <ImportErrorsDrawer
                open={errorDrawerState}
                onClose={handleCloseErrorDrawer}
                errors={selectedErrors}
                batchId={selectedBatchId}
            />

        </Box>
    );

};